import { AlertTriangle, CircleHelp, Search, UserCheck, UserX } from "lucide-react";
import type { ReactNode } from "react";

import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { brebResolvedKind, type BrebKeyResolutionState } from "../../hooks/useBrebKeyResolution";

export interface BrebRecipientConfirmationProps {
  resolution: BrebKeyResolutionState;
  /** The saved client's name, when the payment is going to one. Shown alongside the directory's answer. */
  clientName?: string | null;
  /** One more line under a resolved answer, e.g. the detected key type and the masked document. */
  facts?: ReactNode;
}

type Tone = "neutral" | "success" | "warning" | "danger";

const TONE_CLASS: Record<Tone, string> = {
  neutral: "border-border bg-muted/30 text-muted-foreground",
  success: "border-primary/30 bg-primary/5 text-foreground",
  warning: "border-warning/40 bg-warning/10 text-foreground",
  danger: "border-destructive/40 bg-destructive/10 text-foreground",
};

const ICON_CLASS: Record<Tone, string> = {
  neutral: "text-muted-foreground",
  success: "text-primary",
  warning: "text-warning",
  danger: "text-destructive",
};

/**
 * Who the Bre-B directory says owns the key — the only place the recipient's name appears on this rail.
 *
 * The form stopped asking for a name here, so this is not decoration: it is the answer to "who am I paying".
 * Three consequences shape it.
 *
 * **It is always rendered, at a constant height.** The resolution is debounced, so a block that appeared and
 * disappeared would reflow the form on every pause in typing and move the Continue button under the cursor.
 * Only the contents change.
 *
 * **The name is masked and that is correct.** The directory returns `"Ale*** Pul***"` by construction, which
 * is what every Colombian bank shows for the same reason: enough to recognise your own payee, not enough to
 * enumerate strangers. It is never editable and never sent — `beneficiary_name` travels empty on Bre-B, and
 * the provider ignores it anyway.
 *
 * **An unknown status does not claim a rejection.** `UNKNOWN` is synthesized by our own backend when it could
 * not read a status off the ledger entry; saying "this will be rejected" over it would be asserting something
 * nobody told us.
 */
export function BrebRecipientConfirmation({ resolution, clientName, facts }: BrebRecipientConfirmationProps) {
  const { t } = useTranslations("dispersions");

  const view = resolveView(resolution, t);

  return (
    <div
      aria-live="polite"
      aria-busy={resolution.status === "loading"}
      className={cn(
        "flex min-h-[68px] items-start gap-3 rounded-xl border px-4 py-3 transition-colors",
        TONE_CLASS[view.tone],
      )}
    >
      <view.icon size={16} className={cn("mt-0.5 shrink-0", ICON_CLASS[view.tone])} aria-hidden />
      <div className="min-w-0 flex-1">
        {resolution.status === "loading" ? (
          <>
            <div className="h-3.5 w-40 animate-pulse rounded bg-foreground/10" />
            <div className="mt-2 h-3 w-28 animate-pulse rounded bg-foreground/10" />
            <span className="sr-only">{t("breb_lookup_loading")}</span>
          </>
        ) : (
          <>
            <div className="text-[13px] font-semibold">{view.title}</div>
            {view.detail && <div className="mt-0.5 text-[12px] text-muted-foreground">{view.detail}</div>}
            {facts && resolution.status === "resolved" && (
              <div className="mt-0.5 text-[12px] text-muted-foreground">{facts}</div>
            )}
          </>
        )}
        {/* The saved client's name next to the directory's: the only place a key filed under the wrong
            client becomes visible. Never compared programmatically — with masked names a false "they
            match" is the dangerous answer, and a human reading both lines is the reliable check. */}
        {clientName && resolution.status !== "loading" && (
          <div className="mt-1.5 text-[12px] text-muted-foreground">
            {t("breb_recipient_saved_as", { name: clientName })}
          </div>
        )}
      </div>
    </div>
  );
}

interface View {
  tone: Tone;
  icon: typeof UserCheck;
  title: ReactNode;
  detail?: ReactNode;
}

/**
 * The namespace's own translator, so the twelve keys below are checked against the interface.
 *
 * The parameter was typed `(key: never, …)` and then cast straight back to `(key: string, …)` inside, which
 * removed the one guarantee `useTranslations` exists to give: `t("breb_recipient_idl")` compiled, and what a
 * person confirming who receives their money would have read is the raw key id. Same idiom as
 * `VerifyEmailForm`'s `RegisterTranslator`.
 */
type DispersionsTranslator = ReturnType<typeof useTranslations<"dispersions">>["t"];

function resolveView(resolution: BrebKeyResolutionState, t: DispersionsTranslator): View {
  switch (resolution.status) {
    case "idle":
      return { tone: "neutral", icon: Search, title: t("breb_recipient_idle") };
    case "loading":
      return { tone: "neutral", icon: Search, title: t("breb_lookup_loading") };
    case "not_found":
      return {
        tone: "danger",
        icon: UserX,
        title: t("breb_lookup_not_found"),
        detail: t("breb_lookup_not_found_detail"),
      };
    case "unavailable":
      // `warning`, not `danger`, for the same reason `BREB_STATUS_UNKNOWN` below is: nobody told us the key
      // is bad. Painted red it reads as a rejection, and the operator goes looking for a typo in a key that
      // is probably fine. The hook is already asking the directory again.
      return {
        tone: "warning",
        icon: AlertTriangle,
        title: t("breb_lookup_unavailable"),
        detail: t("breb_lookup_unavailable_detail"),
      };
    case "resolved": {
      const holder = resolution.data.holder_name?.trim();
      const bank = resolution.data.bank_name?.trim();

      // The reading comes from `brebResolvedKind`, which the two GATES also call, and that shared call is
      // the whole point: this switch used to compare `data.status !== "ACTIVE"` itself while the gate
      // upper-cased the same field, so a directory answering `"active"` printed "the key exists but is not
      // active. The payout would be rejected." directly above an enabled Authorize button.
      switch (brebResolvedKind(resolution.data)) {
        case "status_unknown":
          return {
            tone: "warning",
            icon: CircleHelp,
            title: t("breb_status_unknown"),
            detail: holder ? t("breb_recipient_holder_line", { holder, bank: bank || "—" }) : undefined,
          };
        case "inactive":
          return { tone: "warning", icon: AlertTriangle, title: t("breb_lookup_inactive") };
        case "no_holder":
          // The one warning that does NOT block, and the copy says why: the key is active, the directory
          // just withheld the name. Nothing is sent to the runtime for Bre-B anyway — `beneficiary_name`
          // travels empty — so refusing here would stop a send the network will accept.
          return { tone: "warning", icon: CircleHelp, title: t("breb_recipient_no_holder") };
        case "confirmed":
          return {
            tone: "success",
            icon: UserCheck,
            title: holder ?? "",
            detail: t("breb_recipient_confirmed", { bank: bank || "—" }),
          };
      }
    }
  }
}
